import { FlocType } from '@easydone/shared';

export interface TypeHierarchyRules {
  [parentType: string]: FlocType[];
}

/**
 * Defines valid parent-child relationships for FLOC types
 * Based on industry-standard EAM hierarchies (SAP/Maximo/Dynamics 365)
 */
export function getTypeHierarchyRules(): TypeHierarchyRules {
  return {
    // Site: can contain buildings, floors (if no building), systems
    site: ['building', 'floor', 'system'],

    // Building: can contain floors, areas
    building: ['floor', 'area'],

    // Floor: can contain areas, zones
    floor: ['area', 'zone'],

    // Area: can contain zones, systems, equipment positions
    area: ['zone', 'system', 'equipment_position'],

    // Zone: can contain systems, equipment positions
    zone: ['system', 'equipment_position'],

    // System: can only contain equipment positions
    system: ['equipment_position'],

    // Equipment Position: cannot have children (terminal node)
    equipment_position: [],

    // Custom: can contain anything except sites
    custom: ['building', 'floor', 'area', 'zone', 'system', 'equipment_position', 'custom']
  };
}

/**
 * Check if a FLOC type can be a child of another type
 */
export function canBeChildOf(
  childType: FlocType,
  parentType: FlocType | null
): { valid: boolean; reason?: string } {
  // Root level: only sites or custom types allowed
  if (!parentType) {
    return childType === 'site' || childType === 'custom'
      ? { valid: true }
      : { valid: false, reason: 'Only Sites or Custom types can be at root level' };
  }

  const rules = getTypeHierarchyRules();
  const allowedChildren = rules[parentType] || [];

  if (!allowedChildren.includes(childType)) {
    return {
      valid: false,
      reason: `A ${childType.replace('_', ' ')} cannot be a child of a ${parentType.replace('_', ' ')}`
    };
  }

  return { valid: true };
}

/**
 * Get user-friendly description of what can be children of a type
 */
export function getAllowedChildrenDescription(parentType: FlocType): string {
  const rules = getTypeHierarchyRules();
  const allowed = rules[parentType] || [];

  if (allowed.length === 0) {
    return 'This type cannot have children';
  }

  return 'Allowed children: ' + allowed
    .map(t => t.replace('_', ' '))
    .join(', ');
}
